/**
 * Pending-invite handoff between /register → /verify-email → /login.
 *
 * Stored in localStorage (not sessionStorage) so the token survives:
 *   - the email-link click opening a new tab
 *   - tab close + reopen
 *   - the verify-email landing in a fresh tab
 *
 * sessionStorage was the original choice but it is per-tab, and any of the
 * above scenarios produced a silent loss — the user landed on /onboarding
 * instead of being routed back to /invite/:token after login.
 *
 * 24-hour TTL matches the email-verification token lifetime, so a stale
 * entry can't outlive the underlying invitation. Older entries are cleared
 * on read.
 */

const KEY = 'sl_pending_invite';
const TTL_MS = 24 * 60 * 60 * 1000;

interface Entry {
  token: string;
  ts: number;
}

export function setPendingInvite(token: string): void {
  try {
    const entry: Entry = { token, ts: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    // Quota exceeded / storage disabled — silently drop. The invitation is
    // still acceptable via direct /invite/:token visit, just not auto-resumed.
  }
}

export function consumePendingInvite(): string | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const entry = JSON.parse(raw) as Entry;
    localStorage.removeItem(KEY);
    if (typeof entry.token !== 'string' || typeof entry.ts !== 'number') return null;
    if (Date.now() - entry.ts > TTL_MS) return null;
    return entry.token;
  } catch {
    // Legacy plain-string entry from the sessionStorage era — accept once,
    // then forget. Removed above already.
    if (raw && raw.length >= 16) return raw;
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
